import type { HelpContent, HelpTopicContent, HelpTopicId } from './topics';

/** Lower case, and "ё" is "е": the Russian spelling people actually type. */
export function normalize(value: string): string {
  return value.toLocaleLowerCase().replaceAll('ё', 'е');
}

const WORD = /[\p{L}\p{N}#@+_-]+/gu;

function wordsOf(query: string): string[] {
  return [...new Set(normalize(query).match(WORD) ?? [])];
}

/**
 * A query word also matches without its last letter once it has five or more, so "группировка"
 * finds "группировки" and "boards" finds "board". Deterministic and dependency free.
 */
function variants(word: string): string[] {
  return word.length >= 5 ? [word, word.slice(0, -1)] : [word];
}

/** The fields of an article in the order a reader meets them, with how much a match counts. */
function fields(topic: HelpTopicContent): { text: string; weight: number }[] {
  return [
    { text: topic.title, weight: 8 },
    { text: topic.keywords ?? '', weight: 4 },
    { text: topic.lead, weight: 3 },
    { text: topic.tip ?? '', weight: 2 },
    ...(topic.steps ?? []).map((text) => ({ text, weight: 1 })),
    ...(topic.points ?? []).map((text) => ({ text, weight: 1 })),
    { text: topic.example ?? '', weight: 1 },
  ];
}

export interface SearchHit {
  id: HelpTopicId;
  score: number;
  /** A short passage from the article that shows why it matched. */
  excerpt: string;
}

const EXCERPT_LENGTH = 150;

function shorten(text: string, at: number): string {
  if (text.length <= EXCERPT_LENGTH) return text;
  let from = Math.max(0, at - 40);
  if (from > 0) {
    const space = text.indexOf(' ', from);
    if (space !== -1 && space < at) from = space + 1;
  }
  let end = Math.min(text.length, from + EXCERPT_LENGTH);
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end);
    if (space > from) end = space;
  }
  return `${from > 0 ? '… ' : ''}${text.slice(from, end)}${end < text.length ? ' …' : ''}`;
}

function excerptOf(topic: HelpTopicContent, words: readonly string[]): string {
  for (const { text } of fields(topic).slice(2)) {
    const folded = normalize(text);
    let first = -1;
    for (const word of words) {
      for (const variant of variants(word)) {
        const index = folded.indexOf(variant);
        if (index !== -1 && (first === -1 || index < first)) first = index;
      }
    }
    if (first !== -1) return shorten(text, first);
  }
  return shorten(topic.lead, 0);
}

/**
 * The articles that contain EVERY word of the query, best first: a word found in a title counts
 * most, then keywords, the lead and the rest; ties keep the order of the contents. An empty query
 * matches nothing here (callers show the contents instead). Case and "ё" do not matter.
 */
export function searchHelp(
  content: HelpContent,
  order: readonly HelpTopicId[],
  query: string,
): SearchHit[] {
  const words = wordsOf(query);
  if (words.length === 0) return [];
  const hits: (SearchHit & { position: number })[] = [];
  order.forEach((id, position) => {
    const topic = content[id];
    const parts = fields(topic).map((part) => ({ ...part, folded: normalize(part.text) }));
    let score = 0;
    for (const word of words) {
      let best = 0;
      for (const part of parts) {
        if (variants(word).some((variant) => part.folded.includes(variant))) {
          best = Math.max(best, part.weight);
        }
      }
      if (best === 0) return;
      score += best;
    }
    hits.push({ id, score, excerpt: excerptOf(topic, words), position });
  });
  return hits
    .sort((a, b) => b.score - a.score || a.position - b.position)
    .map(({ id, score, excerpt }) => ({ id, score, excerpt }));
}

/** The ids of the matching articles in the order of the contents; everything for an empty query. */
export function searchTopics(
  content: HelpContent,
  order: readonly HelpTopicId[],
  query: string,
): HelpTopicId[] {
  if (wordsOf(query).length === 0) return [...order];
  const found = new Set(searchHelp(content, order, query).map((hit) => hit.id));
  return order.filter((id) => found.has(id));
}

/** The text split into plain and matching pieces, for highlighting without any HTML. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const words = wordsOf(query).flatMap(variants);
  const folded = normalize(text);
  if (words.length === 0 || folded.length !== text.length) return [{ text, match: false }];
  const marked = new Array<boolean>(text.length).fill(false);
  for (const word of words) {
    for (let at = folded.indexOf(word); at !== -1; at = folded.indexOf(word, at + 1)) {
      marked.fill(true, at, at + word.length);
    }
  }
  const parts: { text: string; match: boolean }[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = parts.at(-1);
    if (last && last.match === marked[i]) last.text += text.charAt(i);
    else parts.push({ text: text.charAt(i), match: marked[i] ?? false });
  }
  return parts;
}
